/**
 * 방 이벤트 스트림 훅 — transport + reducer 를 잇는다.
 *
 * 화면 컴포넌트는 여기서 나오는 state 만 본다. 목이냐 실서버냐는
 * lib/transport.ts 가 흡수하므로 이 훅과 화면은 전환에 영향받지 않는다.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { createInitialState, streamReducer, type StreamState } from './reducer'
import { connect, type ConnState, type Transport } from './transport'
import { fetchHistory } from './api'
import { HISTORY_PAGE, USE_MOCK } from './config'
import { FRAME_MS } from './framer'
import type { CaptureSink, ProductionCaptureSink } from './capture'
import type { Envelope } from './events'
import type { RoomAuth } from './token'

export interface UseRoomStreamOptions {
  /** 테스트 주입점 — 기본은 `defaultHistoryFetcher`. 프로덕션은 건드리지 않는다. */
  historyFetcher?: typeof defaultHistoryFetcher
  room: string
  /** 고정 자격 — `auth` 가 있으면 그쪽이 정본이고 이 값은 비어 있어도 된다. */
  key: string
  role: 'host' | 'viewer'
  /** false 면 접속하지 않는다(입장 전 화면 등) */
  enabled?: boolean
  /**
   * 토큰 수명주기(§5.2 — lib/token.ts). 주어지면:
   *  · WS 핸드셰이크·히스토리가 매 시도 `auth.current()` 의 최신 자격을 쓴다
   *  · 4401 시 `auth.refresh()` 로 재교환 후 1회 재시도한다
   *  · 훅이 붙어 있는 동안 선갱신 타이머를 돌린다(만료 2분 전)
   * **identity 가 안정해야 한다**(useMemo) — 아니면 렌더마다 재접속한다.
   */
  auth?: RoomAuth
}

export interface RoomStream {
  state: StreamState
  conn: ConnState
  /** §4 오디오 프레임 송신 싱크 — MicCapture 에 그대로 넘긴다 */
  sink: CaptureSink
  /** §8.3 위로 무한 스크롤 — 더 있으면 true */
  loadMore: () => Promise<boolean>
  /** 클라이언트 유실 구간을 피드에 표시(§4 드롭 시) */
  markClientGap: (
    fromTs: number,
    toTs: number,
    framesLost?: number,
    clientReason?: 'backlog' | 'stall_evict',
  ) => void
  /** §8.5 회의 중 채팅 — name 은 첫 전송에만 실으면 된다 */
  sendChat: (body: string, name?: string) => void
  /**
   * D2 — 서버가 caps 로 알린 VAD 에너지 바닥(dBFS). `null` = 미수신
   * (구 서버·아직 caps 전) → 화면은 `deriveLevelThresholds` 기본값을 쓴다.
   *
   * `otf1Armed` 와 달리 **소켓 성질이 아니라 서버 성질**이므로 재접속에서
   * stale 유지를 요구하지 않는다(`pongSupported` 와 같은 급).
   */
  vadFloorDbfs: number | null
  /**
   * D3 — 업링크 왕복 보고(`pong`). `null` = pong 미수신.
   *
   * 방 이벤트가 아니라 리듀서를 태우지 않는다(transport 의 pong 인터셉트와
   * 같은 규율) — 화면이 "서버가 실제로 받은 것" 을 클라 미터와 나란히
   * 보여 줄 수 있게 여기로 뺀다.
   */
  linkStats: LinkStats | null
}

/** D3 — `pong` 에서 파생한 업링크 상태(서랍이 1줄로 보여 준다). */
export interface LinkStats {
  /** 왕복 시간(ms). `pong.t` 가 숫자가 아니면 `null` — NaN 을 화면에 흘리지 않는다. */
  rttMs: number | null
  /** 서버가 마지막으로 받은 오디오 프레임 seq */
  ack: number | null
  /** 서버가 **받은 바이트로 직접 잰** 마지막 프레임 dBFS */
  serverDbfs: number | null
  lastPongAt: number | null
}

/**
 * 히스토리 1페이지 취득 — `loadMore`·마운트 백필이 공유한다.
 * USE_MOCK 이면 인프로세스 목, 아니면 REST. 테스트는 `historyFetcher` 로
 * 이것을 주입해 동적 import 를 우회한다(vitest 가 mocked dynamic import 를
 * 두 번째 await 에서 resolve 못 하는 것을 DEF-076 검증 중 실측).
 */
export async function defaultHistoryFetcher(
  room: string, cred: string, beforeSeq: number, limit: number,
): Promise<Envelope[]> {
  return USE_MOCK
    ? (await import('../mock/client')).mockHistory(room, beforeSeq, limit)
    : fetchHistory(room, cred, beforeSeq, limit)
}

export function useRoomStream(options: UseRoomStreamOptions): RoomStream {
  const { room, key, role, enabled = true, auth,
          historyFetcher = defaultHistoryFetcher } = options
  const [state, dispatch] = useReducer(streamReducer, undefined, createInitialState)
  const [conn, setConn] = useState<ConnState>('connecting')
  /** D2 — 서버가 알려 준 VAD 바닥. 리듀서를 태우지 않는다(방 이벤트가 아니다). */
  const [vadFloorDbfs, setVadFloorDbfs] = useState<number | null>(null)
  /** D3 — pong 파생 업링크 상태. 역시 리듀서 밖이다. */
  const [linkStats, setLinkStats] = useState<LinkStats | null>(null)
  const transportRef = useRef<Transport | null>(null)
  /** 히스토리 페이지네이션 커서 — 가장 오래된 항목의 seq */
  const oldestRef = useRef<number | null>(null)

  // 피드의 가장 오래된 seq 를 추적한다(히스토리 요청의 before_seq).
  const firstSeq = state.items.find((i) => i.seq !== null)?.seq ?? null
  if (firstSeq !== null && (oldestRef.current === null || firstSeq < oldestRef.current)) {
    oldestRef.current = firstSeq
  }

  useEffect(() => {
    // 자격은 `auth`(토큰 수명주기) 또는 고정 `key` 다. 기본 서버 형상은
    // 구 키를 주지 않아 `key` 가 빈 문자열이다 — auth 가 있으면 그것으로
    // 충분하다(TASK_sec_fix_d).
    if (!enabled || !room || (!key && !auth)) return
    let cancelled = false

    // 토큰 선갱신(§5.2 TTL 10분) — 회의가 붙어 있는 동안만 돈다.
    auth?.start()

    void connect(
      {
        room,
        // 자격은 시도 시점 평가(만료 토큰으로 재접속하지 않는다).
        key: auth ? () => auth.current() : key,
        role,
        // 4401 → 재교환 1회(호스트: /api/token, 뷰어: 패스코드 재교환).
        refreshKey: auth ? () => auth.refresh() : undefined,
      },
      {
      onEvent: (envelope: Envelope) => {
        if (cancelled) return
        dispatch({ type: 'event', envelope })
      },
      // D2 — 서버 능력 통지. 방 이벤트가 아니므로 리듀서를 태우지 않는다.
      onCaps: (caps) => {
        if (cancelled) return
        if (caps.vad_energy_floor_dbfs !== undefined) {
          setVadFloorDbfs(caps.vad_energy_floor_dbfs)
        }
      },
      // D3 — 업링크 왕복 보고. 역시 리듀서 밖이다.
      onPong: (pong) => {
        if (cancelled) return
        const now = Date.now()
        setLinkStats({
          // `t` 가 숫자가 아니면(서버가 null 로 되돌린 경우) 왕복을 모른다 —
          // NaN 을 화면에 흘리지 않는다.
          rttMs: typeof pong.t === 'number' ? now - pong.t : null,
          ack: pong.ack,
          serverDbfs: pong.last_dbfs,
          lastPongAt: now,
        })
      },
      onState: (next) => {
        if (cancelled) return
        setConn(next)
        // ── 외부정지(DEF-018 §1.2) ────────────────────────────────────
        // 종결 상태에서는 선갱신 타이머가 돌 이유가 없다. 죽은 방을 향한
        // 60s 재시도 폭주가 입장 백오프를 채워 **다른 살아있는 방** 입장
        // 까지 막던 것이 DEF-018 이다.
        //
        // `room_gone` 은 여기서 **타이머 정지 신호로만** 쓴다 —
        // `credential_dead`(영구 종결)로 승격하지 않는다(NIT-4). 그 상태는
        // 재접속 소진·판정 불가로도 생기고, 사용자가 새로고침하면 다시
        // 시도할 수 있어야 한다.
        if (next === 'room_gone' || next === 'room_closed'
            || next === 'terminated' || next === 'replaced') {
          auth?.stop()
        }
      },
      },
    ).then((transport) => {
      if (cancelled) {
        transport.close()
        return
      }
      transportRef.current = transport
    })

    return () => {
      cancelled = true
      auth?.stop()
      transportRef.current?.close()
      transportRef.current = null
    }
  }, [room, key, role, enabled, auth])

  // §1 원칙 3 — 오지 않을 자막을 기다리는 "통역 중" 카드를 주기적으로 걷어낸다.
  // 서버 retract 가 연결 단절로 유실될 수 있으므로 화면도 스스로 시간을 잰다.
  useEffect(() => {
    const timer = setInterval(() => {
      dispatch({ type: 'tick', now: Date.now() })
    }, 5_000)
    return () => clearInterval(timer)
  }, [])

  const loadMore = useCallback(async (): Promise<boolean> => {
    const before = oldestRef.current
    if (before === null || before <= 1) return false

    // 히스토리는 **Bearer 자격이 필수**다(§10.1) — 없으면 403 이고, 빈 응답이
    // "더 없음" 과 구분되지 않아 무한 스크롤이 조용히 죽는다. 토큰이 있으면
    // 호출 시점의 최신 토큰을 쓴다(고정 캡처는 10분 뒤 만료 자격이 된다).
    const envelopes = await historyFetcher(
      room, auth ? auth.current() : key, before, HISTORY_PAGE)

    if (envelopes.length === 0) return false
    dispatch({ type: 'prepend-history', envelopes })
    return envelopes.length >= HISTORY_PAGE
  }, [room, key, auth, historyFetcher])

  // DEF-073 — 새 페이지 로드는 과거를 **하나도** 받지 못한다: transport 는
  // `lastSeq > 0` 일 때만 `?since=` 를 붙이고(과거 미전송), `loadMore` 는
  // `oldestRef.current === null` 이면 early return 한다(피드가 비어 있으니
  // 항상 null). 자막은 휘발이 자연스럽지만 **대화는 남아 있을 것으로
  // 기대되는 산출물**이라 마운트 시 1회 백필한다.
  //
  // `before_seq=0` = 최신부터 1페이지. 실패는 **조용히 넘긴다** — 백필이
  // 라이브 자막 경로를 막으면 안 된다(경고만 남긴다).
  const backfilledRef = useRef<string | null>(null)
  useEffect(() => {
    if (!enabled) return
    // 방·자격당 1회만. StrictMode 이중 마운트에 중복 발사되면 안 된다.
    const mark = `${room}:${key}`
    if (backfilledRef.current === mark) return
    backfilledRef.current = mark

    let cancelled = false
    let delivered = false
    void (async () => {
      try {
        const envelopes = await historyFetcher(
          room, auth ? auth.current() : key, 0, HISTORY_PAGE)
        if (cancelled || envelopes.length === 0) return
        dispatch({ type: 'prepend-history', envelopes })
        delivered = true
      } catch (err) {
        console.warn('history backfill failed', err)
      }
    })()

    return () => {
      cancelled = true
      // DEF-076 — StrictMode 이중 마운트: mount1 이 ref 를 찍고 fetch 를
      // 시작한 직후 cleanup 이 오면, 그 결과는 위 `cancelled` 로 버려진다.
      // 그때 ref 를 그대로 두면 mount2 가 "이미 했다" 고 보고 **영영 안
      // 쏜다**(개발 모드에서 백필 0회, 실측). 아직 dispatch 못 했으면 ref 를
      // 풀어 mount2 가 다시 쏘게 한다. 이미 dispatch 했으면 ref 를 유지해
      // 중복 발사를 막는다(프로덕션 빌드 경로 불변).
      if (!delivered && backfilledRef.current === mark) {
        backfilledRef.current = null
      }
    }
  }, [room, key, enabled, auth, historyFetcher])

  const markClientGap = useCallback(
    (
      fromTs: number,
      toTs: number,
      framesLost?: number,
      clientReason?: 'backlog' | 'stall_evict',
    ) => {
      // 클라이언트 드롭은 서버 seq 축 밖의 사건이라 로컬에서만 표시한다.
      // 서버가 seq 갭으로 같은 구간을 알리면 리듀서의 seq 중복 제거가 걸러준다.
      dispatch({
        type: 'event',
        envelope: {
          v: 1,
          type: 'caption.gap',
          room,
          seq: Number.MAX_SAFE_INTEGER - Math.round(toTs / 1000),
          utt: null,
          vis: 'all',
          ts: toTs,
          data: {
            from_ts: fromTs,
            to_ts: toTs,
            // `reason` 은 `'client_gap'` 그대로다 — 사유를 여기 접어 넣으면
            // 리듀서의 `mergeGapInto` 가 사유 교대마다 병합을 거부해 갭
            // 카드가 벽처럼 쌓이고, 갭 카드 본문 문구도 바뀐다.
            reason: 'client_gap',
            ...(framesLost === undefined
              ? {}
              : { frames_lost: framesLost, approx_ms: framesLost * FRAME_MS }),
            ...(clientReason === undefined ? {} : { client_reason: clientReason }),
          },
        },
      })
    },
    [room],
  )

  const sink = useRef<ProductionCaptureSink>({
    send: (data: ArrayBuffer) => transportRef.current?.send(data),
    sendControl: (message: object) => transportRef.current?.sendControl(message),
    get bufferedAmount(): number {
      return transportRef.current?.bufferedAmount ?? 0
    },
    get ready(): boolean {
      return transportRef.current?.ready ?? false
    },
    get otf1Armed(): boolean {
      return transportRef.current?.otf1Armed ?? false
    },
  }).current

  const sendChat = useCallback((body: string, name?: string) => {
    transportRef.current?.sendChat(body, name)
  }, [])

  return {
    state, conn, sink, loadMore, markClientGap, sendChat, vadFloorDbfs, linkStats,
  }
}
